import { useState, useEffect } from 'react'
import { UserRoles, type User, type UserInput, type UserRole } from '@esf/shared'

interface UserFormProps {
  user?: User | null
  onSubmit: (data: UserInput) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
  currentUserRole?: UserRole | null
}

const roleDescriptions: Record<UserRole, string> = {
  SuperAdmin: 'Platform-wide administrator with cross-organisation access',
  OrgAdmin: 'Full access to all features and settings',
  AssetManager: 'Manage products, work orders, and reports',
  FieldTech: 'Execute work orders and update asset status',
  Analyst: 'View dashboards and generate reports',
  PublicViewer: 'View public product passport information',
}

const roleColors: Record<UserRole, string> = {
  SuperAdmin: 'bg-esf-coral/10 text-esf-coral border-esf-coral/20',
  OrgAdmin: 'bg-purple-100 text-purple-700 border-purple-200',
  AssetManager: 'bg-blue-100 text-blue-700 border-blue-200',
  FieldTech: 'bg-green-100 text-green-700 border-green-200',
  Analyst: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  PublicViewer: 'bg-gray-100 text-gray-700 border-gray-200',
}

export default function UserForm({ user, onSubmit, onCancel, isLoading, currentUserRole }: UserFormProps) {
  const [formData, setFormData] = useState<UserInput>({
    email: '',
    name: '',
    role: 'AssetManager',
    phoneNumber: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        name: user.name,
        role: user.role,
        phoneNumber: user.phoneNumber || '',
      })
    }
  }, [user])

  const handleChange = (field: keyof UserInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!formData.role) {
      newErrors.role = 'Role is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const cleanData: UserInput = {
      ...formData,
      phoneNumber: formData.phoneNumber || undefined,
    }

    await onSubmit(cleanData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Email */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={e => handleChange('email', e.target.value)}
          className={`filter-input w-full ${errors.email ? 'border-red-500' : ''}`}
          placeholder="user@example.com"
          disabled={!!user}
        />
        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        {user && <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>}
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={e => handleChange('name', e.target.value)}
          className={`filter-input w-full ${errors.name ? 'border-red-500' : ''}`}
          placeholder="John Smith"
        />
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      {/* Phone Number */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <input
          type="tel"
          value={formData.phoneNumber || ''}
          onChange={e => handleChange('phoneNumber', e.target.value)}
          className="filter-input w-full"
          placeholder="+44 7700 900000"
        />
      </div>

      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Role <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {UserRoles.filter(role => currentUserRole === 'SuperAdmin' || role !== 'SuperAdmin').map(role => (
            <label
              key={role}
              className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                formData.role === role
                  ? `${roleColors[role]} border-2`
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={role}
                checked={formData.role === role}
                onChange={e => handleChange('role', e.target.value)}
                className="sr-only"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{role}</span>
                  {formData.role === role && (
                    <svg className="w-4 h-4 text-esf-navy" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{roleDescriptions[role]}</p>
              </div>
            </label>
          ))}
        </div>
        {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role}</p>}
      </div>

      {/* User Status (only when editing) */}
      {user && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Account Status</h4>
              <p className="text-xs text-gray-500 mt-0.5">
                {user.enabled ? 'User can sign in and access the platform' : 'User is disabled and cannot sign in'}
              </p>
            </div>
            <span className={`badge ${user.enabled ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-600'}`}>
              {user.enabled ? 'Active' : 'Disabled'}
            </span>
          </div>
          {user.lastLoginAt && (
            <p className="text-xs text-gray-500 mt-2">
              Last login: {new Date(user.lastLoginAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </>
          ) : user ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  )
}
